import React from "react";
import { connect } from "react-redux";
import { get, filter, size, map, includes, isEmpty } from "lodash";
import moment from "moment-timezone";
import { withRouter } from "react-router-dom";

import {
  fetchGet,
  fetchPost,
  fetchPatch,
  fetchDelete,
} from "../../../api/fetch";

import OccurrenceForm from "./OccurrenceForm";

import { setToMidnight } from "../../../utils/date";
import {
  getOccurrenceDeparture,
  getOccurrenceArrival,
} from "../../../utils/scheduler";
import {
  getAttributeValue,
  isUnknownProfile,
  unknownProfiles,
} from "../../../utils/profile";
import { HSP, MDC } from "../../../constants/common";

const UpdateOccurrence = (props) => {
  const {
    language,
    open,
    setOpen,
    occurrence,
    timeSlots,
    refresh,
    customTimeSlot,
    parentClose,
    del,
  } = props;

  const type = get(props, ["history", "location", "pathname"]).includes("mdc")
    ? MDC
    : HSP;

  const [isSaving, setIsSaving] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState();
  const [updatedKeys, setUpdatedKeys] = React.useState([]);

  React.useEffect(() => {
    const fetchInitialValues = async () => {
      try {
        const occurrenceStart = getOccurrenceArrival(occurrence);
        //const occurrenceEnd = getOccurrenceDeparture(occurrence);
        const hostProfile = await fetchGet(
          `profiles/${get(
            filter(
              get(occurrenceStart, ["profiles"]),
              (p) => get(p, ["association_type", "id"]) === 1
            ),
            [0, "profile", "id"]
          )}`
        );

        const visitorsIds = map(
          filter(
            get(occurrenceStart, ["profiles"]),
            (p) =>
              get(p, ["association_type", "id"]) === 2 &&
              !isUnknownProfile(get(p, ["profile", "id"]))
          ),
          (p) => get(p, ["profile", "id"])
        );

        const unknownVisitors = map(
          filter(
            get(occurrenceStart, ["profiles"]),
            (p) =>
              get(p, ["association_type", "id"]) === 2 &&
              isUnknownProfile(get(p, ["profile", "id"]))
          ),
          (p, i) => i
        );

        let initialValues = {
          repeatPattern: "noRepeat",
          scheduleName:
            get(occurrence, ["schedule", "name", language]) ||
            get(occurrence, [
              "schedule",
              "name",
              language === "en" ? "fr" : "en",
            ]),
          scheduleType: get(occurrence, ["schedule", "type", "id"]),
          site: get(getOccurrenceArrival(occurrence), [
            "zones",
            0,
            "zone",
            "site",
            "id",
          ]),
          zone: get(getOccurrenceArrival(occurrence), [
            "zones",
            0,
            "zone",
            "id",
          ]),
          startDate: setToMidnight(new Date(get(occurrence, ["when"]))),
          host: hostProfile,
          visitors: visitorsIds,
          unknownVisitors,
          comment: getAttributeValue(occurrence, 19),
        };
        if (customTimeSlot) {
          initialValues.timeType = "custom";
          initialValues.customStart = new Date(get(occurrence, ["when"]));
          
          initialValues.customEnd = new Date(initialValues.customStart.getTime() + get(occurrence, ["events"])[1]["relative_time_min"]*60000);
        } else {
          initialValues.timeType = "timeSlots";
          initialValues.timeSlot = get(
            filter(
              timeSlots,
              (t) =>
                t.start.hours === get(occurrence, ["when"]).hours() &&
                t.start.minutes === get(occurrence, ["when"]).minutes()
            ),
            [0, "id"]
          );
        }
        setInitialValues(initialValues);
      } catch (e) {
        console.log(e);
      }
    };
    fetchInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(occurrence)]);

  const submit = async (formValues, scope) => {
    setIsSaving(true);
    if (scope === "thisoccurrenceonly"){
      try {
        let start, end;
        if (get(formValues, ["timeType"]) === "timeSlots") {
          const timeSlot = get(
            filter(timeSlots, (t) => t.id === get(formValues, ["timeSlot"])),
            [0]
          );
          start = moment(get(formValues, ["startDate"]))
            .add(get(timeSlot, ["start", "hours"]), "h")
            .add(get(timeSlot, ["start", "minutes"]), "m")
            .valueOf();
          end = moment(get(formValues, ["startDate"]))
            .add(get(timeSlot, ["end", "hours"]), "h")
            .add(get(timeSlot, ["end", "minutes"]), "m")
            .valueOf();
        } else if (get(formValues, ["timeType"]) === "custom") {
          start = moment(get(formValues, ["startDate"]))
            .add(get(formValues, ["customStart"]).getHours(), "h")
            .add(get(formValues, ["customStart"]).getMinutes(), "m")
            .valueOf();
          end = moment(get(formValues, ["startDate"]))
            .add(get(formValues, ["customEnd"]).getHours(), "h")
            .add(get(formValues, ["customEnd"]).getMinutes(), "m")
            .valueOf();
        }
  
        let scheduleUpdate = {};
        let occurrenceUpdate = {};
  
        // Update schedule name
        if (includes(updatedKeys, "scheduleName")) {
          scheduleUpdate.name = {
            [language]: get(formValues, ["scheduleName"]),
            [language === "en" ? "fr" : "en"]: "",
          };
        }
  
        // Update occurrence date
        if (
          includes(updatedKeys, "startDate") ||
          includes(updatedKeys, "timeSlot") ||
          includes(updatedKeys, "customStart") ||
          includes(updatedKeys, "customEnd")
        ) {
          occurrenceUpdate.when = start;
        }
  
        // Update comments
        if (includes(updatedKeys, "comment")) {
          occurrenceUpdate.attributes = [
            {
              id: 19,
              value: get(formValues, ["comment"]),
            },
          ];
        }
  
        // Update schedule
        if (!isEmpty(scheduleUpdate)) {
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}`,
            scheduleUpdate
          );
        }
        // Update occurrence
        if (!isEmpty(occurrenceUpdate)) {
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
              occurrence,
              ["id"]
            )}`,
            occurrenceUpdate
          );
        }
  
        // Update events relative_time_minutes
        if (
          includes(updatedKeys, "timeSlot") ||
          includes(updatedKeys, "customStart") ||
          includes(updatedKeys, "customEnd")
        ) {
          const departureEvent = getOccurrenceDeparture(occurrence);
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
              occurrence,
              ["id"]
            )}/events/${get(departureEvent, ["id"])}`,
            {
              relative_time_minutes: (end - start) / 60000,
            }
          );
        }
  
        // Update zone
        if (includes(updatedKeys, "zone")) {
          for (let i = 0; i < size(get(occurrence, ["events"])); i++) {
            await fetchPatch(
              `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
                occurrence,
                ["id"]
              )}/events/${get(occurrence, ["events", i, "id"])}/zones/${get(
                occurrence,
                ["events", i, "zones", 0, "id"]
              )}`,
              { id: get(formValues, ["zone"]) }
            );
          }
        }
  
        // Update host
        if (includes(updatedKeys, "host")) {
          for (let i = 0; i < size(get(occurrence, ["events"])); i++) {
            const hostId = get(
              filter(
                get(occurrence, ["events", i, "profiles"]),
                (p) => get(p, ["association_type", "id"]) === 1
              ),
              [0, "id"]
            );
            await fetchPatch(
              `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
                occurrence,
                ["id"]
              )}/events/${get(occurrence, [
                "events",
                i,
                "id",
              ])}/profiles/${hostId}`,
              { id: get(formValues, ["host", "id"]) }
            );
          }
        }
  
        // Update visitors
        if (
          includes(updatedKeys, "visitors") ||
          includes(updatedKeys, "unknownVisitors")
        ) {
          for (let j = 0; j < size(get(occurrence, ["events"])); j++) {
            const event = get(occurrence, ["events", j]);
  
            const currentVisitors = filter(
              get(event, ["profiles"]),
              (p) => get(p, ["association_type", "id"]) === 2
            );
            console.log("current visitors", currentVisitors);
            // Add missing profiles
            for (let i = 0; i < size(get(formValues, ["visitors"])); i++) {
              const visitor = get(formValues, ["visitors", i]);
              if (
                !isUnknownProfile(visitor) &&
                !includes(
                  map(currentVisitors, (p) => get(p, ["profile", "id"])),
                  visitor
                )
              ) {
                await fetchPost(
                  `schedules/${get(occurrence, [
                    "schedule",
                    "id",
                  ])}/occurrences/${get(occurrence, ["id"])}/events/${get(event, [
                    "id",
                  ])}/profiles`,
                  {
                    id: visitor,
                    association_type_id: 2,
                  }
                );
              }
            }
            // Remove useless profiles
            for (let i = 0; i < size(currentVisitors); i++) {
              const visitor = get(currentVisitors, [i]);
              if (
                !isUnknownProfile(get(visitor, ["profile", "id"])) &&
                !includes(
                  get(formValues, ["visitors"]),
                  get(visitor, ["profile", "id"])
                )
              ) {
                await fetchDelete(
                  `schedules/${get(occurrence, [
                    "schedule",
                    "id",
                  ])}/occurrences/${get(occurrence, ["id"])}/events/${get(event, [
                    "id",
                  ])}/profiles/${get(visitor, ["id"])}`
                );
              }
            }
  
            // Unknown visitors
            const currentUnknownVisitors = size(
              filter(currentVisitors, (v) =>
                isUnknownProfile(get(v, ["profile", "id"]))
              )
            );
            const newUnknownVisitors =
              size(
                filter(get(formValues, ["visitors"]), (v) => isUnknownProfile(v))
              ) + size(get(formValues, ["unknownVisitors"]));
  
            // Add missing unknown visitors
            if (currentUnknownVisitors < newUnknownVisitors) {
              for (
                let i = 0;
                i < newUnknownVisitors - currentUnknownVisitors;
                i++
              ) {
                await fetchPost(
                  `schedules/${get(occurrence, [
                    "schedule",
                    "id",
                  ])}/occurrences/${get(occurrence, ["id"])}/events/${get(event, [
                    "id",
                  ])}/profiles`,
                  {
                    id: get(unknownProfiles, [0]),
                    association_type_id: 2,
                  }
                );
              }
            }
  
            // Remove useless unknown visitors
            if (currentUnknownVisitors > newUnknownVisitors) {
              for (
                let i = 0;
                i < currentUnknownVisitors - newUnknownVisitors;
                i++
              ) {
                const visitor = get(
                  filter(currentVisitors, (v) =>
                    isUnknownProfile(get(v, ["profile", "id"]))
                  ),
                  [i]
                );
                await fetchDelete(
                  `schedules/${get(occurrence, [
                    "schedule",
                    "id",
                  ])}/occurrences/${get(occurrence, ["id"])}/events/${get(event, [
                    "id",
                  ])}/profiles/${get(visitor, ["id"])}`
                );
              }
            }
         }
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      try {
        let start, end;
        if (get(formValues, ["timeType"]) === "timeSlots") {
          const timeSlot = get(
            filter(timeSlots, (t) => t.id === get(formValues, ["timeSlot"])),
            [0]
          );
          start = moment(get(formValues, ["startDate"]))
            .add(get(timeSlot, ["start", "hours"]), "h")
            .add(get(timeSlot, ["start", "minutes"]), "m")
            .valueOf();
          end = moment(get(formValues, ["startDate"]))
            .add(get(timeSlot, ["end", "hours"]), "h")
            .add(get(timeSlot, ["end", "minutes"]), "m")
            .valueOf();
        } else if (get(formValues, ["timeType"]) === "custom") {
          start = moment(get(formValues, ["startDate"]))
            .add(get(formValues, ["customStart"]).getHours(), "h")
            .add(get(formValues, ["customStart"]).getMinutes(), "m")
            .valueOf();
          end = moment(get(formValues, ["startDate"]))
            .add(get(formValues, ["customEnd"]).getHours(), "h")
            .add(get(formValues, ["customEnd"]).getMinutes(), "m")
            .valueOf();
        }
  
        let scheduleUpdate = {};
        let occurrenceUpdate = {};
  
        // Update schedule name
        if (includes(updatedKeys, "scheduleName")) {
          scheduleUpdate.name = {
            [language]: get(formValues, ["scheduleName"]),
            [language === "en" ? "fr" : "en"]: "",
          };
        }
  
        // Update occurrence date
        if (
          includes(updatedKeys, "startDate") ||
          includes(updatedKeys, "timeSlot") ||
          includes(updatedKeys, "customStart") ||
          includes(updatedKeys, "customEnd")
        ) {
          occurrenceUpdate.when = {  "type": "weekly",
  
          "every": 1,
  
          "weekdays": [moment(start).format('dddd').toLowerCase()],
  
          "time": start};
        }
  
        // Update comments
        if (includes(updatedKeys, "comment")) {
          occurrenceUpdate.attributes = [
            {
              id: 19,
              value: get(formValues, ["comment"]),
            },
          ];
        }
  
        // Update schedule
        if (!isEmpty(scheduleUpdate)) {
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}`,
            scheduleUpdate
          );
        }
        // Update occurrence
        if (!isEmpty(occurrenceUpdate)) {
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
              occurrence,
              ["id"]
            )}/?scope=this-and-after-occurrences`,
            occurrenceUpdate
          );
        }

        // Update events relative_time_minutes
        if (
          includes(updatedKeys, "timeSlot") ||
          includes(updatedKeys, "customStart") ||
          includes(updatedKeys, "customEnd")
        ) {
          const departureEvent = getOccurrenceDeparture(occurrence);
          await fetchPatch(
            `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
              occurrence,
              ["id"]
            )}/events/${get(departureEvent, ["id"])}`,
            {
              relative_time_minutes: (end - start) / 60000,
            }
          );
        }

      } catch (e) {
        console.log(e);
      }
    }
    
    
    setIsSaving(false);
    setOpen(false);
    refresh();
  };

  const deleteSchedule = async (occurrences) => {
    setIsSaving(true);
    try {
      // Set occurrence to inactive
      if (occurrences === "futureoccurrences"){
        await fetchPatch(
          `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
            occurrence,
            ["id"]
          )}/?scope=this-and-after-occurrences`,
          {
            active: false,
          }
        );
      } else {
        await fetchPatch(
          `schedules/${get(occurrence, ["schedule", "id"])}/occurrences/${get(
            occurrence,
            ["id"]
          )}`,
          {
            active: false,
          }
        );
      }

    } catch (e) {
      console.log(e);
    }
    setIsSaving(false);
    setOpen(false);
    refresh();
  };

  if (!initialValues) {
    return null;
  }

  return (
    <OccurrenceForm
      open={open}
      setOpen={setOpen}
      parentClose={parentClose}
      setIsSaving={setIsSaving}
      initialValues={initialValues}
      submit={submit}
      timeSlots={timeSlots}
      isSaving={isSaving}
      edition
      deleteSchedule={deleteSchedule}
      updatedKeys={updatedKeys}
      setUpdatedKeys={setUpdatedKeys}
      type={type}
      del={del}
      editView={true}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default withRouter(connect(mapStateToProps)(UpdateOccurrence));
